import { ReactNode, VFC, useEffect, useState } from 'react'
import { CloseCircleIcon, GreenDot } from 'src/assets/svgs'
import { UserIcon } from 'src/components/Image/UserIcon'
import { PopUpMenu } from 'src/components/PopUp/PopUpMenu'
import { Counter, Room, TagMapping } from 'src/models/Room'
import {
  _messageroomborder,
  _mymessagebg,
  _roomactivebg,
  _unreadbatchmessage,
  black,
} from 'src/styles/colors'
import { fontWeightBold, fontWeightMedium } from 'src/styles/font'
import { textEllipsis } from 'src/styles/mixins'
import styled, { css } from 'styled-components'
import { useMessageContext } from '../MessageContext'
import { TagLabelBase, TagsManagement } from '../TagsManagement'

const DELETED_USER_LABEL = '退会済みユーザー'

type RoomItemCommonProps = {
  latestMessage?: {
    content: string
    sentAt?: string
  }
  hidden: boolean
  isRequestReceived: boolean
  hasRead: boolean
  actions: {
    label: string
    onClick: VoidFunction
  }[]
  openRoom: VoidFunction
  isActive?: boolean
  hideRoom: VoidFunction
  showDeletedUserLabel?: boolean
  room: Room
  displayTagsManagement: boolean
  close: () => void
  unreadBatchMessageRoom?: boolean
}
export const RoomItem: VFC<RoomItemCommonProps & { counters: Counter[] }> = ({
  counters,
  ...props
}) =>
  counters.length >= 2 ? (
    <GroupRoomItem {...props} counters={counters} />
  ) : counters.length === 1 ? (
    <SingleRoomItem {...props} counter={counters[0]} />
  ) : (
    <></>
  )

const SingleRoomItem: VFC<RoomItemCommonProps & { counter: Counter }> = ({
  counter: { iconSrc, companyName, position, name, isDeletedUser },
  ...props
}) => {
  return (
    <RoomItemBase
      {...props}
      heading1={companyName}
      heading2={position ? `${name} / ${position}` : name}
      heading1Label={isDeletedUser ? DELETED_USER_LABEL : undefined}
      avatarNode={<UserIcon src={iconSrc} size="48px" />}
    />
  )
}

const GroupRoomItem: VFC<RoomItemCommonProps & { counters: Counter[] }> = ({
  counters,
  ...props
}) => {
  return (
    <RoomItemBase
      {...props}
      heading1={counters[0].companyName}
      heading2={counters[1].companyName}
      heading1Label={counters[0].isDeletedUser ? DELETED_USER_LABEL : undefined}
      heading2Label={counters[1].isDeletedUser ? DELETED_USER_LABEL : undefined}
      avatarNode={
        <MultiUserIcon>
          {counters.map((counter) => (
            <UserIcon key={counter.id} src={counter.iconSrc} size="34px" />
          ))}
        </MultiUserIcon>
      }
    />
  )
}

const TAG_LABEL_LIMIT = 5 // 3点リーダーで切り取る長さです
type RoomItemBaseProps = RoomItemCommonProps & {
  heading1: string
  heading2: string
  heading1Label?: string
  heading2Label?: string
  avatarNode: ReactNode
}
const RoomItemBase: VFC<RoomItemBaseProps> = ({
  avatarNode,
  heading1,
  heading2,
  latestMessage,
  hasRead,
  isRequestReceived,
  actions,
  openRoom,
  isActive,
  hidden,
  hideRoom,
  heading1Label,
  heading2Label,
  displayTagsManagement,
  room,
  close,
  unreadBatchMessageRoom,
}) => {
  const { mutate: mutateRoom } = useMessageContext()
  const [tagMappings, setTagMappings] = useState<TagMapping[]>(room.tagMappings)

  useEffect(() => {
    setTagMappings(room.tagMappings)
  }, [room.tagMappings])

  const displayTagName = (tagName: string) => {
    return tagName.length > TAG_LABEL_LIMIT
      ? tagName.slice(0, TAG_LABEL_LIMIT) + '…'
      : tagName
  }

  return (
    <RoomItemDiv
      isActive={isActive}
      unreadBatchMessageRoom={unreadBatchMessageRoom}
    >
      <RoomItemButton onClick={openRoom}>
        <RoomItemButtonDiv>
          {avatarNode}
          <RoomInfo newMessage={!hasRead || isRequestReceived}>
            <Heading>
              <span>{heading1}</span>

              {heading1Label ? (
                <HeadingLabel>{heading1Label}</HeadingLabel>
              ) : null}
            </Heading>
            <Heading>
              <span>{heading2}</span>

              {heading2Label ? (
                <HeadingLabel>{heading2Label}</HeadingLabel>
              ) : null}
            </Heading>
            <LatestMessage>{latestMessage?.content}</LatestMessage>
          </RoomInfo>
        </RoomItemButtonDiv>
        <TagMappingsDiv>
          {tagMappings.map((tagMapping) => (
            <TagLabel key={tagMapping.tagId}>
              {displayTagName(tagMapping.tagName)}
            </TagLabel>
          ))}
        </TagMappingsDiv>
      </RoomItemButton>
      <AsideArea>
        {!hidden ? (
          <>
            <StyledCloseCircleIcon onClick={hideRoom} />
            {!hasRead ? (
              <GreenDot />
            ) : (
              <div>
                <PopUpMenu actions={actions} />
                {displayTagsManagement && (
                  <TagsManagement
                    tagMappings={tagMappings}
                    setTagMappings={(tagMappings: TagMapping[]) =>
                      setTagMappings([...tagMappings])
                    }
                    mutateRoom={() => mutateRoom && mutateRoom()}
                    roomId={room.id}
                    roomType={room.type}
                    positions={{ top: 100, right: 0 }}
                    close={close}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <div />
        )}
        <div>{latestMessage?.sentAt}</div>
      </AsideArea>
    </RoomItemDiv>
  )
}
const MultiUserIcon = styled.div`
  position: relative;
  width: 48px;
  height: 100%;

  > * {
    :nth-child(1) {
      position: absolute;
      top: 12px;
      right: 0;
    }
    :nth-child(2) {
      position: absolute;
      bottom: 12px;
      left: 0;
    }
  }
`

export const MessageOverview = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-top: 4px;
  line-break: anywhere;
`

const Heading = styled.p`
  ${textEllipsis};
  display: flex;
  align-items: center;
`
const HeadingLabel = styled.span`
  color: #fff;
  padding: 2px 8px;
  font-size: 12px;
  background: #bebebe;
  border-radius: 12px;
  margin-left: 4px;
`

const LatestMessage = styled(MessageOverview)`
  color: ${black};
`

export const RoomInfo = styled.div<{ newMessage?: boolean }>`
  margin-top: 4px;
  font-size: 12px;
  font-weight: ${fontWeightMedium};
  line-height: 1.25;
  ${({ newMessage }) =>
    newMessage &&
    css`
      ${Heading} {
        font-weight: ${fontWeightBold};
      }
      ${LatestMessage} {
        font-weight: ${fontWeightBold};
        color: ${_mymessagebg};
      }
    `}
`
export const RoomItemDiv = styled.div<{
  isActive?: boolean
  unreadBatchMessageRoom?: boolean
}>`
  position: relative;
  display: flex;
  padding: 6px 4px 4px 8px;
  border: 1px solid ${_messageroomborder};
  ${({ unreadBatchMessageRoom }) =>
    unreadBatchMessageRoom
      ? css`
          border: 2px solid ${_unreadbatchmessage};
        `
      : css`
          border: 1px solid ${_messageroomborder};
        `}
  border-radius: 5px;
  :hover {
    background: ${_roomactivebg};
  }
  ${({ isActive }) =>
    isActive &&
    css`
      background: ${_roomactivebg};
    `}

  ${RoomInfo} {
    height: 100%;
    width: 65%;
    margin-left: 16px;
  }
  aside {
    position: absolute;
    top: 6px;
    right: 4px;
    bottom: 4px;
    width: 40px;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    align-items: flex-end;
    margin-left: 12px;
    font-size: 10px;
  }
`
const RoomItemButtonDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  /* NOTE: OS-1073 safari v13 で表示崩れが起きるため絶対値指定する */
  height: 74px;
`
const RoomItemButton = styled.button`
  width: 100%;
`
const AsideArea = styled.aside`
  width: 40px;
`
const StyledCloseCircleIcon = styled(CloseCircleIcon)`
  cursor: pointer;
`
const TagMappingsDiv = styled.div`
  flex-wrap: wrap;
  display: flex;
  margin-right: 30px;
`
const TagLabel = styled.div`
  ${TagLabelBase};
  margin-right: 4px;
  margin-bottom: 4px;
`
